<template>
	<Grid rowGap="medium">
		<GridColumn>
			<FieldSelect
				name="dataType"
				:error="v$.type.$error"
				:options="dataTypesOptions"
				:model-value="formData.dataType"
				@update:model-value="handleDataTypeChange"
				placeholder="Tabular Data"
				:disabled="readOnly"
			>
				<Icon name="grid" />

				Data Type <a href="#" target="_blank">Type Definitions</a>
			</FieldSelect>
		</GridColumn>

		<GridColumn>
			<FieldSelect
				:error="v$.subtype.$error"
				:options="subTypeOptions"
				name="subType"
				v-model.trim="formData.subType"
				placeholder="Select"
				:disabled="readOnly"
			>
				<Icon name="grid" />

				Data Subtype
			</FieldSelect>
		</GridColumn>

		<GridColumn>
			<Checkboxes>
				<FieldCheckbox
					name="reuse"
					v-model="formData.reuse"
					isDropdown
					:disabled="readOnly"
				>
					This material is re-used from a public or private source
				</FieldCheckbox>

				<FieldCheckbox
					name="optional"
					v-model="formData.optional"
					isDropdown
					:disabled="readOnly"
				>
					Compliance policies for this material are not currently enforced
				</FieldCheckbox>

				<FieldCheckbox
					name="hasReadMe"
					v-model="formData.hasReadMe"
					isDropdown
					:disabled="readOnly"
					:allowNA="true"
				>
					This material has a "Read Me"
				</FieldCheckbox>
			</Checkboxes>
		</GridColumn>

		<GridColumn>
			<Field
				name="labSource"
				placeholder="Enter the name of the source of the material"
				v-model="formData.source"
				:disabled="readOnly"
			>
				<Icon name="microscope" />

				Source (Name of Lab or Commercial Source)
			</Field>
		</GridColumn>

		<GridColumn>
			<Field
				name="catalog"
				placeholder="enter the catalog number if available"
				v-model="formData.catalogNumber"
				:disabled="readOnly"
			>
				<Icon name="flask" />

				Catalog Number
			</Field>
		</GridColumn>

		<GridColumn>
			<Field
				name="rrid"
				placeholder="Enter RRID if available"
				v-model="formData.RRID"
				:disabled="readOnly"
			>
				<Icon name="key" />

				Research Resource Identifier (RRID)
			</Field>
		</GridColumn>

		<GridColumn>
			<Field
				name="comments"
				v-model="formData.comments"
				:disabled="readOnly"
			>
				<Icon name="comment" />

				Additional Comments or Instructions
			</Field>
		</GridColumn>

		<GridColumn v-if="userRoleWeight >= 1000 && formData.reuse">
			<Field
				name="associatedReference"
				v-model="formData.associatedReference"
				:disabled="readOnly"
			>
				<Icon name="document" />

				Associated Reference
			</Field>
		</GridColumn>

		<GridColumn v-if="userRoleWeight >= 1000">
			<Field
				name="headSentence"
				v-model="formData.headSentence"
				:disabled="readOnly"
			>
				<Icon name="document" />

				Associated Head Sentence
			</Field>
		</GridColumn>
	</Grid>
</template>

<script>
/**
 * External Dependencies
 */
import useVuelidate from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { mapGetters } from 'vuex'

/**
 * Internal Dependencies
 */
import Icon from '@/components/icon/icon';
import Field from '@/components/field/field';
import Grid, { GridColumn } from '@/components/grid/grid';
import Checkboxes from '@/components/checkboxes/checkboxes';
import FieldSelect from '@/components/field-select/field-select';
import FieldCheckbox from '@/components/field-checkbox/field-checkbox';

export default {
	/**
	 * Name
	 */
	name: 'formDatasetsMaterial',

	/**
	 * Components
	 */
	components: {
		Grid,
		Icon,
		Field,
		Checkboxes,
		GridColumn,
		FieldSelect,
		FieldCheckbox,
	},

	/**
	 * Props
	 */
	props: {
		formData: {
			type: Object,
			default: () => {}
		},
		bestPracticesText: {
			type: String,
			default: () => {}
		},
		readOnly: {
			type: Boolean,
			default: false
		}
	},

	/**
	 * Data
	 */
	data() {
		return {
			showBestPractices: false,
		}
	},

	/**
	 * Computed
	 */
	computed: {
		...mapGetters('account', ['userRoleWeight', ]),
		...mapGetters('pdfViewer', ['dataTypes']),
		dataTypesOptions() {		
			return Object.keys(this.dataTypes.dataTypes).map(key => ({
					value: key,
					label: key,
				}));
		},
		subTypeOptions() {
			const subTypes = this.dataTypes.dataTypes[this.formData.dataType];

			return subTypes.map(type => ({
					value: type,
					label: type,
				})).concat([{
					value: '',
					label: 'None',
				}]);
		}
	},

	/**
	 * Setup
	 */
	setup() {
		// @vuelidate/core is opt-in per component: this is what replaces
		// the global Vue.use(Vuelidate) that vuelidate 0.x installed.
		return { v$: useVuelidate() };
	},

	/**
	 * Validations
	 */
	validations: {
		type: {
			required
		},
		subtype: {
			required
		},
		permalink: {
			required
		}
	},

	/**
	 * Methods
	 */
	methods: {
		// Reset the sub type value when the main datatype changes
		handleDataTypeChange(value) {
			this.formData.dataType = value;
			this.formData.subType = '';
		}
	}
}
</script>

